/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

// Use consistent styling
import 'sanitize.css/sanitize.css';

// Import root app
import { App } from 'app';

import { HelmetProvider } from 'react-helmet-async';

import { store, persistor } from './store';

import reportWebVitals from 'reportWebVitals';

// Initialize languages
import './locales/i18n';

Bugsnag.start({
  apiKey:
    process.env.REACT_APP_BUGSNAG_API_KEY || 'fb483e6cf46d77c19c1259256e084761',
  plugins: [new BugsnagPluginReact()],
  enabledReleaseStages: ['production', 'staging'],
  appVersion: process.env.REACT_APP_VERSION,
  autoTrackSessions: false,
});

const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

ReactDOM.render(
  <ErrorBoundary>
    <Provider store={store}>
      <HelmetProvider>
        <React.StrictMode>
          <PersistGate loading={null} persistor={persistor}>
            <App />
          </PersistGate>
        </React.StrictMode>
      </HelmetProvider>
    </Provider>
  </ErrorBoundary>,
  MOUNT_NODE,
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
